import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { Fragment, useState, useRef, useLayoutEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { enumStatutFormation } from '../../enums/enumStatutFormation'
import { FormationListItem } from '../listItem/formationListItem'
import './cursusThumbnail.css'

export const CursusThumbnail = (props) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [measured, setMeasured] = useState(false);
    const contentRef = useRef(null);
    const measureFullRef = useRef(null);
    const measureCollapsedRef = useRef(null);
    const [fullHeight, setFullHeight] = useState(null);
    const [collapsedHeight, setCollapsedHeight] = useState(null);
    const [collapsing, setCollapsing] = useState(false);
    const [manualMaxHeight, setManualMaxHeight] = useState(null);
    const [hideExtras, setHideExtras] = useState(false);

    // Créer un tableau unique avec toutes les formations
    const allFormations = [];
    
    props.etudiant.formationAVenir?.forEach(f => {
        allFormations.push({ ...f, formation: f.formation, statut: enumStatutFormation.AVENIR });
    });
    
    props.etudiant.formationEnCours?.forEach(f => {
        allFormations.push({ ...f, statut: enumStatutFormation.ENCOURS });
    });
    
    props.etudiant.formationSuivie?.sort((a, b) => b.anneeDiplome - a.anneeDiplome).forEach(f => {
        allFormations.push({ ...f, statut: enumStatutFormation.SUIVIE });
    });

    const showToggle = allFormations.length > 2;
    // En visible on ne rend que les 2 premiers en collapsed pour réduire le DOM
    const formationsToShow = expanded ? allFormations : allFormations.slice(0, 2);

    // Mesure via clones cachés pour éviter flash / incohérences
    useLayoutEffect(() => {
        // Attendre que les refs existent
        const fullEl = measureFullRef.current;
        const colEl = measureCollapsedRef.current;
        const container = contentRef.current;
        if (!fullEl || !colEl || !container) return;

        // Ensure the hidden measurement clones use the same width as the visible container.
        // Without forcing width, offscreen clones can have 0 width and give incorrect heights
        // on narrow screens. We set width imperatively and update on resize.
        const applyWidth = () => {
            const w = Math.max(0, Math.round(container.getBoundingClientRect().width));
            try {
                fullEl.style.width = `${w}px`;
                colEl.style.width = `${w}px`;
            } catch (e) {
                // defensive: ignore if style can't be set
            }
        };

        applyWidth();

        // Mesurer hauteurs après avoir fixé la largeur
        const full = fullEl.scrollHeight;
        const collapsed = colEl.scrollHeight;

        // Ajout minuscule pour éviter couper exactement les bordures
        const padding = 2;
        setFullHeight(full + padding);
        setCollapsedHeight(collapsed + padding);
        setMeasured(true);

        const onResize = () => {
            applyWidth();
            const f = fullEl.scrollHeight;
            const c = colEl.scrollHeight;
            setFullHeight(f + padding);
            setCollapsedHeight(c + padding);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [props.etudiant.formationAVenir, props.etudiant.formationEnCours, props.etudiant.formationSuivie]);
    // Toggle handler: when collapsing, keep full content rendered, animate max-height to collapsedHeight,
    // then after transition ends actually set expanded=false which will render only two items.
    const handleToggle = () => {
        const el = contentRef.current;
        if (!measured || !el) {
            // fallback simple toggle
            setExpanded(e => !e);
            return;
        }

        if (expanded) {
            // first, hide extras visually so the 3rd item disappears before the animation
            setHideExtras(true);

            // small delay to allow CSS opacity change to take effect before we animate the height
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const onTransitionEnd = (ev) => {
                        if (ev.propertyName === 'max-height') {
                            // finish collapse: stop collapsing state and actually hide extra items in DOM
                            setCollapsing(false);
                            setManualMaxHeight(null);
                            setExpanded(false);
                            setHideExtras(false);
                            el.removeEventListener('transitionend', onTransitionEnd);
                        }
                    };

                    // attach listener first to avoid missing the event
                    el.addEventListener('transitionend', onTransitionEnd);

                    // set start height explicitly to fullHeight then switch to collapsedHeight to animate
                    setManualMaxHeight(fullHeight);
                    setCollapsing(true);
                    // next frame set target to collapsed
                    requestAnimationFrame(() => setManualMaxHeight(collapsedHeight));
                });
            });
        } else {
            // expanding: show all items then let measured style handle animation
            setExpanded(true);
            // ensure any collapsing flags cleared
            setCollapsing(false);
            setManualMaxHeight(null);
        }
    };

    return (
        <Card className="p-shadow-5 cursus-card">
            <h1 className="vue-candidat-card-title">
                <Trans i18nKey='profile.formation.singular' />
            </h1>
            {/* visible content: render only collapsed or full items */}
            <div
                ref={contentRef}
                className={`formation-content ${collapsing ? 'is-collapsing' : ''} ${hideExtras ? 'hide-extras' : ''}`}
                style={{
                    maxHeight: manualMaxHeight != null ? `${manualMaxHeight}px` : (measured ? (expanded ? `${fullHeight}px` : `${collapsedHeight}px`) : undefined),
                }}
            >
                {formationsToShow.map((f, index) => {
                    // normalize formation and anneeDiplome props so different shapes (e.g. en cours)
                    const formationProp = f.formation || f;
                    const anneeProp = f.anneeDiplome ?? (f.formation && f.formation.anneeDiplome) ?? null;
                    const keyId = f.id ?? (f.formation && f.formation.id) ?? index;
                    return (
                        <div key={`${keyId}~${index}`} className="formation-item-wrapper">
                            <FormationListItem 
                                formation={formationProp}
                                anneeDiplome={anneeProp}
                                statut={f.statut}
                            />
                            {index < formationsToShow.length - 1 && <Divider />}
                        </div>
                    );
                })}
            </div>

            {/* Hidden measurement clones (kept out of flow) */}
            <div aria-hidden="true" className="formation-measure" style={{position: 'absolute', left: -9999, top: 0, visibility: 'hidden'}} ref={measureFullRef}>
                {allFormations.map((f, index) => {
                    const formationProp = f.formation || f;
                    const anneeProp = f.anneeDiplome ?? (f.formation && f.formation.anneeDiplome) ?? null;
                    const keyId = f.id ?? (f.formation && f.formation.id) ?? `mfull~${index}`;
                    return (
                        <div key={`mfull~${keyId}~${index}`} className="formation-item-wrapper">
                            <FormationListItem 
                                formation={formationProp}
                                anneeDiplome={anneeProp}
                                statut={f.statut}
                            />
                            {index < allFormations.length - 1 && <Divider />}
                        </div>
                    );
                })}
            </div>

            <div aria-hidden="true" className="formation-measure" style={{position: 'absolute', left: -9999, top: 0, visibility: 'hidden'}} ref={measureCollapsedRef}>
                {allFormations.slice(0,2).map((f, index) => {
                    const formationProp = f.formation || f;
                    const anneeProp = f.anneeDiplome ?? (f.formation && f.formation.anneeDiplome) ?? null;
                    const keyId = f.id ?? (f.formation && f.formation.id) ?? `mcol~${index}`;
                    return (
                        <div key={`mcol~${keyId}~${index}`} className="formation-item-wrapper">
                            <FormationListItem 
                                formation={formationProp}
                                anneeDiplome={anneeProp}
                                statut={f.statut}
                            />
                            {index < 1 && <Divider />}
                        </div>
                    );
                })}
            </div>
            {showToggle && (
                <div className="formation-toggle-container">
                    <Button 
                        label={expanded ? t('general.see_less') : t('general.see_more')}
                        className="formation-toggle-btn"
                        onClick={handleToggle}
                        text
                    />
                </div>
            )}
        </Card>
    )
}